import * as THREE from 'three';
import {
    rotatingSet,
    type RoomPlanner,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {addToHistoryStackHelper} from 'components/customer/RoomPlanner/helpers/history';
import type {HistoryActionContext} from './historyActionContext';
import type {
    CabinetMesh,
    HistoryStack,
} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';

const ROTATION_EPSILON = 0.0001;

type PositionLike = THREE.Vector3 | {x: number; y: number; z: number};
type RotationLike =
    THREE.Euler | {x: number; y: number; z: number; order?: string} | null;

const getPositionValues = (
    pos: PositionLike
): {
    x: number;
    y: number;
    z: number;
} => {
    return {
        x: pos instanceof THREE.Vector3 ? pos.x : pos.x,
        y: pos instanceof THREE.Vector3 ? pos.y : pos.y,
        z: pos instanceof THREE.Vector3 ? pos.z : pos.z,
    };
};

const getDirection = (rotation: number) => {
    const normalizedRotation =
        ((rotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    if (Math.abs(normalizedRotation) < 0.1) return 'normal';
    if (Math.abs(normalizedRotation - Math.PI / 2) < 0.1) return 'right';
    if (Math.abs(normalizedRotation - Math.PI) < 0.1) return 'back';
    if (Math.abs(normalizedRotation - (3 * Math.PI) / 2) < 0.1) return 'left';
    return 'normal';
};

const getRotationValues = (
    r: RotationLike
): {x: number; y: number; z: number} | null => {
    if (r == null) return null;
    return {x: r.x, y: r.y, z: r.z};
};

const isRotationZDifferent = (a: RotationLike, b: RotationLike): boolean => {
    const va = getRotationValues(a);
    const vb = getRotationValues(b);
    if (va == null || vb == null) return va !== vb;
    return Math.abs(va.z - vb.z) > ROTATION_EPSILON;
};

const isRotationYDifferent = (a: RotationLike, b: RotationLike): boolean => {
    const va = getRotationValues(a);
    const vb = getRotationValues(b);
    if (va == null || vb == null) return va !== vb;
    return Math.abs(va.y - vb.y) > ROTATION_EPSILON;
};

export interface AddPositionChangeToHistoryPayload {
    position: PositionLike;
    rotation: RotationLike;
    id: string | number;
    index?: number;
    initialPosition: PositionLike;
    initialRotation?: RotationLike;
    roomIndex?: number;
    roomCabNumber?: number;
    typeName?: string;
    isPlaceholder?: boolean;
}

const formatCabinetTitlePrefix = (
    payload: AddPositionChangeToHistoryPayload
): string => {
    const {roomIndex, roomCabNumber, typeName} = payload;
    const cabNum = roomCabNumber != null ? String(roomCabNumber) : null;
    const displayRoomIndex = roomIndex != null ? String(roomIndex + 1) : null;
    const prefix =
        displayRoomIndex != null && cabNum != null
            ? `[${displayRoomIndex}-${cabNum}] `
            : cabNum != null
              ? `[${cabNum}] `
              : '';
    const name = (typeName?.trim() || 'Cabinet').trim();
    return prefix ? `${prefix}${name}` : name;
};

export const addPositionChangeToHistory = (
    state: RoomPlanner,
    payload: AddPositionChangeToHistoryPayload
): void => {
    const {position, rotation, id, index, initialPosition, initialRotation} =
        payload;
    const pos =
        position instanceof THREE.Vector3
            ? position.clone()
            : new THREE.Vector3(position.x, position.y, position.z);
    const rot =
        rotation == null
            ? new THREE.Euler(0, 0, 0, 'XYZ')
            : rotation instanceof THREE.Euler
              ? rotation.clone()
              : new THREE.Euler(
                    rotation.x,
                    rotation.y,
                    rotation.z,
                    ((rotation as {order?: string})
                        .order as THREE.EulerOrder) || 'XYZ'
                );

    const cabinetKey = `${id}_${index ?? 'default'}`;

    const positionChangedThreshold = 25;

    // Compare against initialPosition (actual current position before update) instead of history
    // This ensures we get the correct previous position even after undo/redo
    const initialPos = getPositionValues(initialPosition);
    const currentPos = getPositionValues(position);

    const deltaX = Math.abs(currentPos.x - initialPos.x);
    const deltaY = Math.abs(currentPos.y - initialPos.y);
    const deltaZ = Math.abs(currentPos.z - initialPos.z);

    if (!state.accumulatedPositionDeltas[String(cabinetKey)]) {
        state.accumulatedPositionDeltas[String(cabinetKey)] = {
            x: 0,
            y: 0,
            z: 0,
        };
    }

    const accumulated = state.accumulatedPositionDeltas[String(cabinetKey)];
    accumulated.x += deltaX;
    accumulated.y += deltaY;
    accumulated.z += deltaZ;

    // Compare rotation against initialRotation (actual current rotation before update)
    const initialRot =
        initialRotation instanceof THREE.Euler
            ? initialRotation
            : initialRotation != null
              ? new THREE.Euler(
                    initialRotation.x,
                    initialRotation.y,
                    initialRotation.z,
                    ((initialRotation as {order?: string})
                        .order as THREE.EulerOrder) || 'XYZ'
                )
              : new THREE.Euler(0, 0, 0, 'XYZ');

    const rotationChanged = payload.isPlaceholder
        ? isRotationYDifferent(initialRot, rot)
        : isRotationZDifferent(initialRot, rot);

    const positionOverThreshold =
        accumulated.x > positionChangedThreshold ||
        accumulated.y > positionChangedThreshold ||
        accumulated.z > positionChangedThreshold;

    if (!positionOverThreshold && !rotationChanged) {
        return;
    }

    state.accumulatedPositionDeltas[String(cabinetKey)] = {
        x: 0,
        y: 0,
        z: 0,
    };

    const cabinetLabel = formatCabinetTitlePrefix(payload);
    const title = rotationChanged
        ? `**Rotate** ${cabinetLabel}`
        : `**Move** ${cabinetLabel}`;

    // Use initialPosition/initialRotation from payload - these are the actual current
    // cabinet position/rotation before the update (captured from Redux state or scene)
    const previousPosition =
        initialPosition instanceof THREE.Vector3
            ? initialPosition.clone()
            : new THREE.Vector3(
                  initialPosition.x,
                  initialPosition.y,
                  initialPosition.z
              );

    const previousRotation =
        initialRotation != null
            ? initialRotation instanceof THREE.Euler
                ? initialRotation.clone()
                : new THREE.Euler(
                      initialRotation.x,
                      initialRotation.y,
                      initialRotation.z,
                      ((initialRotation as {order?: string})
                          .order as THREE.EulerOrder) || 'XYZ'
                  )
            : new THREE.Euler(0, 0, 0, 'XYZ');

    addToHistoryStackHelper(
        state,
        {
            action: 'POSITION_CHANGE',
            isActive: true,
            timestamp: Date.now(),
            title,
            data: {
                rotation: rot,
                position: pos,
                previousRotation,
                previousPosition,
                id,
                index,
                changeType: rotationChanged ? 'rotation' : 'position',
                isPlaceholder: Boolean(payload.isPlaceholder),
            },
        },
        {clearAfterActive: true}
    );
};

const applyPositionAndRotationToCabinet = (
    cabinetGroup: CabinetMesh,
    position: THREE.Vector3 | {x: number; y: number; z: number},
    rotation: THREE.Euler | {x: number; y: number; z: number; order?: string}
): void => {
    const newPosition =
        position instanceof THREE.Vector3
            ? position.clone()
            : new THREE.Vector3(position.x, position.y, position.z);
    cabinetGroup.position.copy(newPosition);

    const newRotation =
        rotation instanceof THREE.Euler
            ? rotation.clone()
            : new THREE.Euler(
                  rotation.x,
                  rotation.y,
                  rotation.z,
                  ((rotation as {order?: string}).order as THREE.EulerOrder) ||
                      'XYZ'
              );
    const isPlaceholder = Boolean(cabinetGroup.userData?.isPlaceholder);
    if (isPlaceholder) {
        cabinetGroup.rotation.y = newRotation.y;
    } else {
        cabinetGroup.rotation.z = newRotation.z;
    }
    const isRotating = store.getState().roomPlanner.isRotating;
    store.dispatch(rotatingSet(!isRotating));
    cabinetGroup.userData.currentRotation = cabinetGroup.rotation;
    const direction = getDirection(
        isPlaceholder ? newRotation.y : newRotation.z
    );
    const directionMap: Record<string, string> = {
        normal: 'front',
        right: 'left',
        left: 'right',
        back: 'back',
    };
    cabinetGroup.userData.currentDirection =
        directionMap[String(direction)] ?? 'front';
    cabinetGroup.updateMatrixWorld();
    autoSave(cabinetGroup, true);
};

const meshesFromRef = (
    ref: {current?: CabinetMesh[] | undefined} | undefined
): CabinetMesh[] => {
    const current = ref?.current;
    return Array.isArray(current) ? current : [];
};

const findPositionChangeTarget = (
    historyItem: HistoryStack,
    ctx: HistoryActionContext
): CabinetMesh | undefined => {
    const id = historyItem.data?.id;
    if (id == null) return undefined;
    if (historyItem.data?.isPlaceholder) {
        return meshesFromRef(
            ctx.placeholderListRef as {current?: CabinetMesh[]} | undefined
        ).find((item) => String(item.userData?.id) === String(id));
    }
    return meshesFromRef(ctx.cabinetGroupListRef).find(
        (cabinet) => Number(cabinet.userData?.values?.job_cabinet_id) == id
    );
};

export const undoPositionChange = (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): CabinetMesh | undefined => {
    if (!historyItem?.data) return;
    if (historyItem.action !== 'POSITION_CHANGE') return;
    const {data} = historyItem;
    const prevPos = data.previousPosition ?? data.position;
    const prevRot = data.previousRotation ?? data.rotation;
    if (!prevPos || !data.id) return;
    const cabinetGroup = findPositionChangeTarget(historyItem, ctx);
    if (!cabinetGroup) return;
    applyPositionAndRotationToCabinet(
        cabinetGroup,
        prevPos,
        prevRot ?? new THREE.Euler(0, 0, 0, 'XYZ')
    );
    ctx.setPreviousHistoryIndex(targetIndex);
    return cabinetGroup;
};

export const redoPositionChange = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<CabinetMesh | undefined> => {
    if (!historyItem?.data) return;
    if (historyItem.action !== 'POSITION_CHANGE') return;
    const {data} = historyItem;
    if (!data.position || !data.id) return;
    const cabinetGroup = findPositionChangeTarget(historyItem, ctx);
    if (!cabinetGroup) return;
    applyPositionAndRotationToCabinet(
        cabinetGroup,
        data.position,
        data.rotation ?? new THREE.Euler(0, 0, 0, 'XYZ')
    );
    await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
    return cabinetGroup;
};
